import React from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';
import formTypes from 'components/HomePage/formTypeSelectValues';
import ActivityPerms from 'components/AccessControl/permissions.json';
import AccessControl from 'components/AccessControl';

const SearchPanel = ({ title, searchCriteria, onSearchCriteriaChanged, onSearchClicked, permissions }) => {
  const onSearchClick = () => {
    onSearchClicked(searchCriteria);
  };

  const onInputKeyUp = (e) => {
    if (e.keyCode === 13) {
      onSearchClick();
    }
  };

  const onGridChange = (e) => {
    const newSearchCriteria = { ...searchCriteria };
    newSearchCriteria.gridType = e.target.value;
    onSearchCriteriaChanged(newSearchCriteria);
  };

  const onSearchQueryChanged = (e) => {
    const newSearchCriteria = { ...searchCriteria };
    newSearchCriteria.searchQuery = e.target.value;
    onSearchCriteriaChanged(newSearchCriteria);
  };

  const onAutoCompleteChange = (_, lookupValue, propertyName) => {
    if ((typeof lookupValue === 'string' || lookupValue instanceof String)) {
      searchCriteria[propertyName] = lookupValue;
      searchCriteria[`${ propertyName }Selected`] = false;
    } else if (lookupValue !== null) {
      searchCriteria[propertyName] = lookupValue;
      searchCriteria[`${ propertyName }Selected`] = true;
    }
    onSearchCriteriaChanged(searchCriteria);
  };

  const onFormTypeChange = (e) => {
    searchCriteria.formType = e.target.value;
    onSearchCriteriaChanged(searchCriteria);
  };

  return (
    <div className="panel search-panel">
      <div className="panel-header">
        <div className="panel-title">
          { title }
        </div>
      </div>
      <div className="panel-body">
        <div className="row row-top">
          <div className="col">
            <label className="form-radio">
              <input
                type="radio"
                name="gridType"
                value="forms"
                checked={ searchCriteria.gridType === 'forms' }
                onChange={ e => onGridChange(e) }
              />
              <i className="form-icon" /> Forms
            </label>
            { AccessControl.hasPermission(permissions, [ActivityPerms.customerPageView, ActivityPerms.customerEdit])
              && <label className="form-radio">
                <input
                  type="radio"
                  name="gridType"
                  value="customers"
                  checked={ searchCriteria.gridType === 'customers' }
                  onChange={ e => onGridChange(e) }
                />
                <i className="form-icon" /> Customers
              </label>}
            { AccessControl.hasPermission(permissions, [ActivityPerms.accountPageView, ActivityPerms.accountEdit])
              && <label className="form-radio">
                <input
                  type="radio"
                  name="gridType"
                  value="accounts"
                  checked={ searchCriteria.gridType === 'accounts' }
                  onChange={ e => onGridChange(e) }
                />
                <i className="form-icon" /> Accounts
              </label>}
            { AccessControl.hasPermission(permissions, [ActivityPerms.accountPageView, ActivityPerms.customerPageView], true)
            && <label className="form-radio">
              <input
                type="radio"
                name="gridType"
                value="legalEntities"
                checked={ searchCriteria.gridType === 'legalEntities' }
                onChange={ e => onGridChange(e) }
              />
              <i className="form-icon" /> Legal Entities
            </label>}
          </div>
        </div>
        <div className="row search-row">
          <div className="col-2 p-2">
            <input
              className="form-input"
              type="search"
              name="SearchText"
              placeholder={ searchCriteria.gridType === 'forms' ? 'Search Reference' : 'Search Text' }
              value={ searchCriteria.searchQuery || '' }
              onChange={ e => onSearchQueryChanged(e) }
              onKeyUp={ e => onInputKeyUp(e) }
            />
          </div>
          <div className="col-2 p-2">
            <AutoCompleteInput
              className="form-input"
              id="legal-entity-id"
              name="legalEntity"
              category="legalentity"
              placeholder="Legal Entity Name"
              value={ searchCriteria.legalEntity || '' }
              activeOnly={ false }
              onChange={ (_, value) => onAutoCompleteChange(null, value, 'legalEntity') }
              onSelectedItem={ value => onAutoCompleteChange(null, value, 'legalEntity') }
            />
          </div>
          <div className="col-2 p-2">
            <AutoCompleteInput
              className="form-input"
              id="account-number"
              name="accountNumber"
              category="account"
              placeholder="Account Number"
              value={ searchCriteria.accountNumber || '' }
              activeOnly={ false }
              onChange={ (_, value) => onAutoCompleteChange(null, value, 'accountNumber') }
              onSelectedItem={ value => onAutoCompleteChange(null, value, 'accountNumber') }
            />
          </div>
          <div className="col-2 p-2">
            <AutoCompleteInput
              className="form-input"
              id="customer-id"
              name="customerId"
              category="customer"
              placeholder="Customer ID"
              value={ searchCriteria.customer || '' }
              activeOnly={ false }
              onChange={ (_, value) => onAutoCompleteChange(null, value, 'customer') }
              onSelectedItem={ value => onAutoCompleteChange(null, value, 'customer') }
              disabled={ searchCriteria.gridType === 'accounts' }
            />
          </div>
          <div className="col-2 p-2">
            <select
              className="form-select"
              disabled={ searchCriteria.gridType !== 'forms' }
              value={ searchCriteria.formType }
              onChange={ e => onFormTypeChange(e) }
            >
              <option value="">All Forms</option>
              {formTypes.map((option, i) => (
                <option key={ option.value } value={ option.value }>
                  {option.description}
                </option>
              ))}
            </select>
          </div>
          <div className="col p-2">
            <button
              id="search-btn"
              type="button"
              className="btn btn-primary search-btn"
              onClick={ () => onSearchClick() }
            ><span className="icon icon-search" /> Search</button>
          </div>
        </div>
      </div>
    </div>
  );
};

SearchPanel.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string
  ]),
  searchCriteria: PropTypes.shape().isRequired,
  onSearchCriteriaChanged: PropTypes.func.isRequired,
  onSearchClicked: PropTypes.func.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string)
};

SearchPanel.defaultProps = {
  title: 'Search',
  permissions: null
};

const SearchPanelForm = reduxForm({
  form: 'searchForm',
})(SearchPanel);

const mapDispatchToProps = dispatch => ({ dispatch });

const ConnectedSearchPanel = connect(
  ({ forms }) => ({
    forms: forms.forms,
    formsCount: forms.formsCount,
    isAddFormModalShown: forms.isAddFormModalShown
  }),
  mapDispatchToProps
)(SearchPanelForm);

export default ConnectedSearchPanel;
